import { useEffect, useState } from "react";
import { Users, Warning } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Compact card that lists teammates already scheduled off during the picked
 * date range. Used on both self-service and admin leave forms so requesters
 * / managers see the coverage impact BEFORE submit. Silent when there are
 * no valid dates yet — no chatter for empty state.
 */
export default function TeamAvailabilitySidebar({ start, end, employeeId }) {
  const [state, setState] = useState({ loading: false, data: null });

  useEffect(() => {
    if (!start || !end) { setState({ loading: false, data: null }); return; }
    if (end < start) { setState({ loading: false, data: null }); return; }
    setState((s) => ({ ...s, loading: true }));
    const params = new URLSearchParams({ start, end });
    if (employeeId) params.set("employee_id", employeeId);
    api.get(`/hr/team-availability?${params.toString()}`)
      .then((r) => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: null }));
  }, [start, end, employeeId]);

  if (!start || !end || !state.data) return null;
  const items = state.data.items || [];
  const dept = state.data.department;

  return (
    <div className="mb-6 border border-zinc-200 bg-zinc-50" data-testid="availability-sidebar">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-white">
        <Users size={14} className="text-zinc-500" />
        <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 flex-1">
          Team availability · {dept || "your dept"} · {start} → {end}
        </div>
        {state.loading && <span className="text-[10px] font-mono text-zinc-400">CHECKING…</span>}
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-3 text-xs text-zinc-500 flex items-center gap-2" data-testid="availability-clear">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
          Nobody else in {dept || "your team"} is off during these dates. Coverage looks clear.
        </div>
      ) : (
        <div>
          <div className="px-4 py-2 text-xs text-amber-800 flex items-center gap-2 bg-amber-50" data-testid="availability-warning">
            <Warning size={14} />
            <strong>{items.length}</strong>&nbsp;colleague{items.length > 1 ? "s are" : " is"} already scheduled off — coverage may be thin.
          </div>
          <ul className="divide-y divide-zinc-100">
            {items.map((it) => (
              <li key={it.id} className="px-4 py-2 text-xs flex items-center justify-between" data-testid={`availability-item-${it.id}`}>
                <div>
                  <div className="font-medium">{it.employee_name}</div>
                  <div className="text-[10px] font-mono text-zinc-400">
                    {it.employee_no || "—"} · {it.type}{it.half_day ? ` · ${it.half_day.toUpperCase()}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="tnum">{it.start_date} → {it.end_date}</div>
                  <span className={`text-[9px] font-mono uppercase ${it.status === "approved" ? "text-emerald-700" : "text-amber-700"}`}>
                    {it.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
